// useBoardNodelistHydration.js

import { useCallback, useMemo } from 'react';

import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';
import {
  getNodelistNodeTitle,
  normalizeNodelistKeys,
} from 'utilities/NodelistField';
import { isRelationFieldType } from 'utilities/FieldTypes';

/**
 * Batches related-node title hydration for visible Relation card fields.
 *
 * @param {object} params
 * @param {object[]|null|undefined} params.nodes - Cards that may reference related nodes.
 * @param {Map|null|undefined} params.schemaFieldMap - Field definitions by key.
 * @param {string[]|null|undefined} params.fieldKeys - Visible card field keys to inspect.
 * @param {function|null|undefined} params.resolveFieldValue - Resolves a field value from a node.
 *
 * @returns {{ resolveNodelistTitle: (nodeKey: string) => string }}
 */
export default function useBoardNodelistHydration({
  nodes,
  schemaFieldMap,
  fieldKeys,
  resolveFieldValue,
} = {}) {
  const relatedKeys = useMemo(() => {
    const lowerMap = new Map();
    if (schemaFieldMap?.forEach) {
      schemaFieldMap.forEach((fieldDef, key) => {
        const lower = String(key || '').trim().toLowerCase();
        if (lower && !lowerMap.has(lower)) lowerMap.set(lower, fieldDef);
      });
    }

    const nodelistFields = (Array.isArray(fieldKeys) ? fieldKeys : [])
      .map((fieldKey) => (
        schemaFieldMap?.get?.(fieldKey)
        || lowerMap.get(String(fieldKey || '').trim().toLowerCase())
        || null
      ))
      .filter((fieldDef) => isRelationFieldType(fieldDef));

    if (!nodelistFields.length || typeof resolveFieldValue !== 'function') return [];

    const keys = new Set();
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      nodelistFields.forEach((fieldDef) => {
        normalizeNodelistKeys(resolveFieldValue(node, fieldDef)).forEach((key) => {
          keys.add(key);
        });
      });
    });

    return [...keys];
  }, [fieldKeys, nodes, resolveFieldValue, schemaFieldMap]);

  const { nodesByKey, isLoading } = useNodesByKeysQuery(relatedKeys, {
    enabled: relatedKeys.length > 0,
  });

  const resolveNodelistTitle = useCallback((nodeKey) => {
    const key = String(nodeKey || '').trim();
    if (!key) return '';

    const related = nodesByKey?.get?.(key);
    if (related) return getNodelistNodeTitle(related);

    // Avoid flashing raw Firestore keys while the batch fetch is in flight.
    if (isLoading) return '';

    return 'Unavailable';
  }, [isLoading, nodesByKey]);

  return { resolveNodelistTitle };
}
